import { apiOrganizationLists } from "@/api/organization-list.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiOrganizationListType,
  ApiOrganizationListUpdatePayload,
} from "@/types/organization-list.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const organizationListsKeys = createQueryKeys("organizationLists", {
  all: null,
  byType: (type: ApiOrganizationListType) => ({
    queryKey: [type],
    queryFn: async () => {
      const result = await apiOrganizationLists.getByType(type);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  byId: (listId: number) => ({
    queryKey: [listId],
    queryFn: async () => {
      const result = await apiOrganizationLists.getById(listId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  items: (listId: number) => ({
    queryKey: [listId, "items"],
    queryFn: async () => {
      const result = await apiOrganizationLists.getItems(listId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data || [];
    },
  }),
});

export const useOrganizationListByType = (
  type: ApiOrganizationListType,
  enabled: boolean = true
) => {
  return useQuery({
    ...organizationListsKeys.byType(type),
    staleTime: 60 * 1000,
    enabled,
  });
};

export const useOrganizationListById = (listId: number, enabled: boolean = true) => {
  return useQuery({
    ...organizationListsKeys.byId(listId),
    staleTime: 60 * 1000,
    enabled: enabled && listId > 0,
  });
};

export const useOrganizationListItems = (listId: number, enabled: boolean = true) => {
  return useQuery({
    ...organizationListsKeys.items(listId),
    staleTime: 60 * 1000,
    enabled: enabled && listId > 0,
  });
};

export const useJobTracks = (enabled: boolean = true) => {
  return useOrganizationListByType(ApiOrganizationListType.job_catalog_tracks, enabled);
};

export const useJobGrades = (enabled: boolean = true) => {
  return useOrganizationListByType(ApiOrganizationListType.job_catalog_grades, enabled);
};

export interface OrganizationListSaveParams {
  listId: number;
  payload: ApiOrganizationListUpdatePayload;
}

export const useOrganizationListSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ listId, payload }: OrganizationListSaveParams) => {
      const result = await apiOrganizationLists.update(listId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (organizationList) => {
      queryClient.setQueryData(
        organizationListsKeys.byType(organizationList.type).queryKey,
        organizationList
      );
      queryClient.setQueryData(
        organizationListsKeys.byId(organizationList.id).queryKey,
        organizationList
      );
      queryClient.setQueryData(
        organizationListsKeys.items(organizationList.id).queryKey,
        organizationList.items
      );
      void queryClient.invalidateQueries({
        queryKey: organizationListsKeys.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};
